import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Plus, AlertCircle, Trash2, Edit, X, ArrowUpRight, CheckCircle, Search } from 'lucide-react'
import { ConfirmModal } from '../components/ConfirmModal'

function NCModal({ nc, onClose, onSave, isSaving }) {
  const [form, setForm] = useState(nc || { 
    source: '', 
    source_id: '', 
    identification_date: '', 
    description: '', 
    root_cause: '', 
    corrective_action: '', 
    owner: '', 
    target_date: '', 
    status: 'Open' 
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h2>{nc ? 'Edit Non-Conformity / CAPA' : 'Report Non-Conformity / CAPA'}</h2>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Source *</label>
              <select className="form-select" value={form.source} onChange={e => set('source', e.target.value)}>
                <option value="">Select Source...</option>
                {['Internal Audit', 'External Audit', 'Management Review', 'Security Incident', 'Customer Complaint', 'Self-Identification'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Target Closure Date</label>
              <input type="date" className="form-input" value={form.target_date} onChange={e => set('target_date', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description of Non-Conformity *</label>
            <textarea className="form-textarea" value={form.description} onChange={e => set('description', e.target.value)} placeholder="What went wrong?" />
          </div>
          <div className="form-group">
            <label className="form-label">Root Cause Analysis</label>
            <textarea className="form-textarea" value={form.root_cause} onChange={e => set('root_cause', e.target.value)} placeholder="Why did it happen? (5 Whys or similar)" />
          </div>
          <div className="form-group">
            <label className="form-label">Corrective & Preventive Action (CAPA)</label>
            <textarea className="form-textarea" value={form.corrective_action} onChange={e => set('corrective_action', e.target.value)} placeholder="Action plan to prevent recurrence..." />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Assignee / Owner</label>
              <input className="form-input" value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="Process Owner" />
            </div>
            <div className="form-group">
              <label className="form-label">Initial Status</label>
              <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
                {['Open', 'In Progress', 'Verify', 'Closed'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!form.description || !form.source || isSaving} onClick={() => onSave(form)}>
            {isSaving ? 'Submitting…' : nc ? 'Update Record' : 'Log Non-Conformity'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function NonConformity({ toast }) {
  const qc = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [filter, setFilter] = useState('All')

  const { data: ncs = [], isLoading } = useQuery({ queryKey: ['non-conformities'], queryFn: api.getNonConformities })

  const createMut = useMutation({
    mutationFn: api.createNonConformity,
    onSuccess: () => { qc.invalidateQueries(['non-conformities']); setShowModal(false); toast('NC reported successfully', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => api.updateNonConformity(id, data),
    onSuccess: () => { qc.invalidateQueries(['non-conformities']); setEditing(null); toast('NC updated successfully', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const deleteMut = useMutation({
    mutationFn: ({ id, reason }) => api.deleteNonConformity(id, reason),
    onSuccess: () => { qc.invalidateQueries(['non-conformities']); setConfirmDelete(null); toast('NC moved to bin', 'success') },
    onError: e => toast(e.message, 'error'),
  })

  const filtered = filter === 'All' ? ncs : ncs.filter(nc => nc.status === filter)

  const statusBadge = (s) => ({
    'Open': 'badge-danger', 'In Progress': 'badge-warning', 
    'Verify': 'badge-info', 'Closed': 'badge-success'
  }[s] || 'badge-neutral')

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Non-Conformity & CAPA</h1>
          <p>Continuous improvement through tracking failures and implementing corrective actions</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={15} /> Log Non-Conformity
        </button>
      </div>

      <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {['All', 'Open', 'In Progress', 'Verify', 'Closed'].map(s => (
            <button key={s} className={`btn btn-sm ${filter === s ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(s)}>
              {s}
            </button>
          ))}
        </div>
        <div className="stat-value" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
          {filtered.length} Findings found
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 20 }}>
        {isLoading && <div className="empty-state">Loading...</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="empty-state" style={{ gridColumn: '1/-1', padding: 60 }}>
            <AlertCircle size={48} color="var(--border)" />
            <p>No non-conformities found for the selected filter.</p>
          </div>
        )}
        {filtered.map(nc => (
          <div key={nc.id} className="card shadow-sm" style={{ padding: 0, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface-hover)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="font-mono text-xs" style={{ color: 'var(--text-muted)' }}>NC-{String(nc.id).padStart(4,'0')}</span>
                <span className={`badge badge-sm ${statusBadge(nc.status)}`}>{nc.status}</span>
              </div>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Source: {nc.source}</span>
            </div>
            <div style={{ padding: '20px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)' }}>{nc.description}</h3>
              
              {nc.root_cause && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>Root Cause</div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{nc.root_cause}</p>
                </div>
              )}

              {nc.corrective_action && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--success)', textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle size={12} /> CAPA Plan
                  </div>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>{nc.corrective_action}</p>
                </div>
              )}
            </div>
            <div style={{ padding: '12px 20px', background: 'var(--bg-base)', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem' }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <div>Owner: <span style={{ fontWeight: 600 }}>{nc.owner || '—'}</span></div>
                <div>Target: <span style={{ fontWeight: 600 }}>{nc.target_date || '—'}</span></div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="btn btn-ghost btn-sm" style={{ padding: 4 }} onClick={() => setEditing(nc)}>
                  <Edit size={14} />
                </button>
                <button className="btn btn-ghost btn-sm" style={{ padding: 4, color: 'var(--danger)' }} onClick={() => setConfirmDelete(nc)}>
                  <Trash2 size={14} />
                </button>
                <button className="btn btn-ghost btn-sm" style={{ padding: 4 }}>
                  <ArrowUpRight size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <NCModal 
          onClose={() => setShowModal(false)}
          onSave={data => createMut.mutate(data)}
          isSaving={createMut.isPending}
        />
      )}
      {editing && (
        <NCModal 
          nc={editing}
          onClose={() => setEditing(null)}
          onSave={data => updateMut.mutate({ id: editing.id, data })}
          isSaving={updateMut.isPending}
        />
      )}
      <ConfirmModal 
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={(reason) => deleteMut.mutate({ id: confirmDelete.id, reason })}
        title="Move to Bin?"
        message={`Delete Non-Conformity "${confirmDelete?.description?.substring(0, 40)}..."? It will be moved to the Recycle Bin and can be restored later.`}
        showReason={true}
      />
    </div>
  )
}
